import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { CommonFilter } from '@/src/utilities/common/headerFilterUtils'

type DisabledIngredientsState = {
  loading: boolean
  data: DisabledIngredientsByRestaurantDto[]
  count: number
  error: SerializedError | null
}

type State = {
  disabledIngredients: DisabledIngredientsState
}

// New API structure types
export type DisabledIngredientDetailDto = {
  id?: number // Optional for backward compatibility, use productCompanyId if not available
  productCompanyId: number
  productCompanyName: string
  status: string
  isPermanentDisable: boolean
  daysElapsed: number | null // null for permanent
  createdAt: string
  reason: string
  createdBy: number
  userName: string
  userRoleId: number
  userRoleName: string
  updatedBy?: number
  updatedAt?: string
  updatedByUserName?: string | null
}

export type DisabledIngredientsByRestaurantDto = {
  restaurantId: number
  restaurantName: string
  state: string
  ownership: string
  timeZone?: string
  totalDisabledIngredients: number
  permanentDisabledCount: number
  temporaryDisabledCount: number
  averageDaysDisabled: number | null // null if no temporary
  maxDaysDisabled: number | null // null if no temporary
  ingredients: DisabledIngredientDetailDto[]
}

// Legacy type kept for backward compatibility (deprecated)
export type RestoDisabledIngredients = {
  restaurantId: number
  restaurantName: string
  productCompanyId: number
  productCompanyName: string
  status: string
  createdBy: number
  userName: string
  userRoleId: number
  userRoleName: string
  createdAt: string
  daysElapsed: number
  state(state: string): CommonFilter[]
  ownership(ownership: string): CommonFilter[]
  reason: string
}

const initialState: State = {
  disabledIngredients: {
    loading: false,
    data: [],
    count: 0,
    error: null
  }
}

type fetchDisabledIngredientsParams = {
  countryId: number
}

export const fetchDisabledIngredients = createAsyncThunk<
  DisabledIngredientsByRestaurantDto[] | null,
  fetchDisabledIngredientsParams,
  Redux
>('api/fetchDisabledIngredients', async (fetchDisabledIngredientsParams, { extra }) => {
  try {
    const response = await extra.httpClient(
      `resto365-cerebro-product-company-override/disabled-ingredients/${fetchDisabledIngredientsParams.countryId}`
    )

    const {
      data: { data: disabledIngredients }
    } = response

    // API now returns restaurants grouped with nested ingredients
    return disabledIngredients as DisabledIngredientsByRestaurantDto[]
  } catch (error) {
    throw new Error('Failed to fetch Disabled Ingredients')
  }
})

type markAsPermanentParams = {
  id: number
  reason: string
}

export const markIngredientAsPermanent = createAsyncThunk<any, markAsPermanentParams, Redux>(
  'api/markIngredientAsPermanent',
  async ({ id, reason }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.put(`resto365-cerebro-product-company-override/mark-permanent/${id}`, {
        reason
      })

      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return rejectWithValue({ message: 'Ingredient not found', code: 'NOT_FOUND' })
      }
      if (error.response?.status === 403) {
        return rejectWithValue({ message: 'You do not have permission to perform this action', code: 'FORBIDDEN' })
      }
      if (error.response?.status === 401) {
        return rejectWithValue({ message: 'Unauthorized', code: 'UNAUTHORIZED' })
      }

      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to mark as permanently disabled',
        code: 'ERROR'
      })
    }
  }
)

const disabledIngredientsReducer = createSlice({
  name: 'disabledIngredients',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchDisabledIngredients.pending, state => {
        state.disabledIngredients.loading = true
        state.disabledIngredients.error = null
      })
      .addCase(fetchDisabledIngredients.fulfilled, (state, action) => {
        state.disabledIngredients.loading = false
        state.disabledIngredients.data = action.payload || []

        // Count is now restaurant count, not ingredient count
        state.disabledIngredients.count = action.payload?.length || 0
      })
      .addCase(fetchDisabledIngredients.rejected, (state, action) => {
        state.disabledIngredients.loading = false
        state.disabledIngredients.error = action.error || null
      })
  }
})

export default disabledIngredientsReducer.reducer
